import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft } from "lucide-react";

/**
 * Public Privacy Policy page. Required by Twilio toll-free verification (the
 * "Privacy Policy URL" field on Step 3/3).
 */
export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-[#0B1220] text-white py-10 px-6">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-stone-300 hover:text-white mb-4"
          >
            <ArrowLeft size={12} /> Back to app
          </Link>
          <div className="canales-overline text-stone-300 flex items-center gap-2">
            <ShieldCheck size={12} /> Legal · Privacy Policy
          </div>
          <h1 className="font-display text-3xl md:text-5xl mt-2 leading-[1.05]">
            Privacy Policy
          </h1>
          <p className="text-stone-300 mt-3 max-w-2xl text-sm md:text-base">
            How Canales Franchise LLC handles the personal data of employees,
            franchise partners, and store visitors who interact with the
            internal audit platform.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <P><strong>Effective date:</strong>{" "}{new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</P>

        <H>1. Who we are</H>
        <P>
          This Privacy Policy describes how <strong>Canales Franchise LLC</strong>{" "}
          (d/b/a Mueblería Canales), a U.S. furniture retailer based in Texas,
          collects, uses, stores, and shares personal information in connection
          with its internal operational audit platform.
        </P>

        <H>2. Information we collect</H>
        <P>
          The platform is used <strong>only by authorized employees</strong> and
          franchise partners. The data we collect about them is limited to:
        </P>
        <ul className="list-disc pl-6 space-y-1 text-[14.5px] text-stone-700">
          <li>Full name and work email address</li>
          <li>Mobile phone number (optional, for SMS operational notifications)</li>
          <li>Assigned store / role / permission level</li>
          <li>Audit actions performed (question responses, photos, comments, signatures)</li>
          <li>System access logs (IP address, browser, timestamps) for security purposes</li>
        </ul>
        <P>
          We do <strong>not</strong> collect personal data from store customers
          or any external consumer audience via this platform.
        </P>

        <H>3. How we use the information</H>
        <ul className="list-disc pl-6 space-y-1 text-[14.5px] text-stone-700">
          <li>To allow employees to authenticate and use the audit platform.</li>
          <li>
            To send <strong>operational SMS notifications</strong> (audit
            findings, task assignments, follow-up reminders) — only to phone
            numbers explicitly captured and toggled by the company
            administrator. See the{" "}
            <Link to="/sms-terms" className="underline font-medium">SMS Terms &amp; Opt-In page</Link>{" "}
            for full details.
          </li>
          <li>To generate signed PDF audit reports and statistical dashboards.</li>
          <li>To investigate security incidents and abuse of the platform.</li>
        </ul>

        <H>4. How we share the information</H>
        <P>
          We do <strong>not</strong> sell, rent, or trade personal data. We
          share information only with:
        </P>
        <ul className="list-disc pl-6 space-y-1 text-[14.5px] text-stone-700">
          <li>
            <strong>Service providers</strong> strictly necessary to operate the
            platform — for example Twilio (SMS delivery) and our cloud hosting
            provider. These providers are contractually bound to handle data
            only on our instructions and to maintain industry-standard security
            controls.
          </li>
          <li>
            <strong>Government authorities</strong> when required by U.S. law,
            subpoena, or court order.
          </li>
        </ul>

        <H>5. Data retention</H>
        <P>
          Personal data is retained for as long as the employee or franchise
          partner remains active on the platform, plus the period required for
          accounting and legal recordkeeping (typically 5 years). Audit photos
          and PDF reports are stored as part of the business operational record.
        </P>

        <H>6. Cookies</H>
        <P>
          Our platform uses <strong>essential cookies only</strong>: secure
          session tokens required for authentication (httpOnly) and a local
          preference that remembers your language and cookie choice. We do{" "}
          <strong>not</strong> use advertising, analytics, or third-party
          tracking cookies. A consent banner is shown on first visit; you can
          clear your browser storage at any time to reset your choice.
        </P>

        <H>7. Security</H>
        <P>
          We protect personal data with industry-standard measures: encrypted
          transport (HTTPS), bcrypt password hashing, JWT-based session tokens,
          role-based access control, and restricted database access. No system
          is 100% secure, but we take reasonable steps to prevent unauthorized
          access, disclosure, or loss.
        </P>

        <H>8. Your rights</H>
        <P>
          You may request access to, correction of, or deletion of your personal
          data at any time by writing to{" "}
          <a href="mailto:antonio@canalesfranchise.com" className="underline">
            antonio@canalesfranchise.com
          </a>
          . You may also revoke SMS consent at any time by replying{" "}
          <strong>STOP</strong> to any message we send, or by asking your
          administrator to remove your phone number from the platform.
        </P>

        <H>9. Contact</H>
        <P>
          For any privacy-related question, write to:
          <br />
          <strong>Canales Franchise LLC</strong>
          <br />
          Attn: Privacy
          <br />
          <a href="mailto:antonio@canalesfranchise.com" className="underline">
            antonio@canalesfranchise.com
          </a>
        </P>

        <H>10. Updates</H>
        <P>
          We may update this Privacy Policy from time to time. The “Effective
          date” at the top reflects the most recent revision.
        </P>

        <div className="text-xs text-stone-500 pt-6 border-t border-stone-200 mt-8">
          © {new Date().getFullYear()} Canales Franchise LLC · All rights reserved.
        </div>
      </main>
    </div>
  );
}

function H({ children }) { return <h2 className="font-display text-2xl mt-6 mb-2 text-stone-900">{children}</h2>; }
function P({ children }) { return <p className="text-[14.5px] text-stone-700 leading-relaxed">{children}</p>; }
